import React from "react";
import { Box, Typography, Button } from "@mui/material";
import InboxIcon from "@mui/icons-material/Inbox";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

/**
 * Unified empty-state block. Two visual tiers:
 * - variant="block" (default): a page/section-level empty state — icon,
 *   optional title, description, optional action. Use `dashed` to toggle
 *   the dashed onboarding-style border vs a plain soft panel.
 * - variant="compact": a single-line empty state for inside an existing
 *   card/table area (smaller icon, no border/background of its own).
 *
 * `action` (optional): { label, onClick } — rendered as a button. Use this
 * whenever there's something the user can actually do about the emptiness,
 * e.g. "Clear filters" for a filtered-empty result, or "Complete KYC" for
 * an onboarding gate.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  variant = "block",
  dashed = true,
}) {
  const isCompact = variant === "compact";
  const iconEl = icon || (
    <InboxIcon sx={{ fontSize: isCompact ? "1.5rem" : "2.5rem", color: "#cbd5e1" }} />
  );

  return (
    <Box
      sx={{
        textAlign: "center",
        py: isCompact ? 3 : 5,
        px: 3,
        ...(isCompact
          ? {}
          : {
              border: dashed ? "2px dashed #e2e8f0" : "none",
              bgcolor: "#f8fafc",
              borderRadius: "10px",
            }),
      }}
    >
      <Box mb={isCompact ? 1 : 1.5}>{iconEl}</Box>
      {title && (
        <Typography variant="h6" color="#475569" gutterBottom>
          {title}
        </Typography>
      )}
      <Typography variant="body2" color="#94a3b8">
        {description}
      </Typography>
      {action && (
        <Box mt={isCompact ? 1.5 : 2.5}>
          <Button variant={isCompact ? "text" : "contained"} size="small" onClick={action.onClick}>
            {action.label}
          </Button>
        </Box>
      )}
    </Box>
  );
}

/**
 * Small inline pill for field/sub-section-level emptiness (e.g. "PCC not
 * yet requested" inside an otherwise-populated card). Deliberately muted —
 * these are expected, normal states, not errors.
 */
export function EmptyCapsule({ text, icon }) {
  return (
    <Box display="flex" justifyContent="center" py={0.5}>
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{
          px: 2.5,
          py: 1,
          borderRadius: "999px",
          bgcolor: "#f1f5f9",
          color: "#94a3b8",
        }}
      >
        {icon || <HourglassEmptyIcon sx={{ fontSize: "1rem" }} />}
        <Typography variant="body2" fontWeight={500}>
          {text}
        </Typography>
      </Box>
    </Box>
  );
}
